import { useState, useEffect } from 'preact/hooks';

/**
 * Custom hook to handle an asset being favourited or unfavourited.
 *
 * @param {string | number} userId   The user's ID
 * @param {string | number} entityId  The asset's ID
 * @returns {Object}                An object with:
 *   - isFavourited: boolean
 *   - loading: boolean
 *   - error: any
 *   - favourite(): function
 *   - unfavourite(): function
 */
export function useFavourite({
  userId,
  entityId,
}: {
  userId?: number;
  entityId?: string | number;
}) {
  const [isFavourited, setIsFavourited] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [favouriteCount, setFavouriteCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Fetch whether the asset is currently favourited.
  useEffect(() => {
    if (!userId || !entityId) return;
    const entityType = 'book';

    setLoading(true);
    setError(null);

    fetch(
      `/api/v0/favourites/u/${userId}/isfavourite/${entityType}/${entityId}`,
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to check favourite status: ${response.status}`,
          );
        }
        const result = await response.json();
        setIsFavourited(result.value);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [userId, entityId]);

  // Fetch the total number of favourites for this entity
  useEffect(() => {
    if (!entityId) return;
    const entityType = 'book';

    setLoading(true);
    setError(null);

    fetch(`/api/v0/favourites/count/${entityType}/${entityId}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to get favourite count: ${response.status}`);
        }
        const result = await response.json();
        setFavouriteCount(result.count);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [entityId]);

  // Call the `favourite` endpoint and update state accordingly.
  const setFavourite = async () => {
    if (!userId || !entityId) return;
    if (isFavourited) return;
    try {
      setLoading(true);
      setError(null);

      const entityType = 'book';

      const response = await fetch('/api/v0/favourites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, entityId, entityType }),
      });

      if (!response.ok) {
        throw new Error(`Failed to favourite asset: ${response.status}`);
      }
      setIsFavourited(true);
      setFavouriteCount(favouriteCount + 1);
    } catch (err) {
      setError(err as string);
    } finally {
      setLoading(false);
    }
  };

  // Call the `unfavourite` endpoint and update state accordingly.
  const setUnfavourite = async () => {
    if (!userId || !entityId) return;
    if (!isFavourited) return;

    try {
      setLoading(true);
      setError(null);

      const entityType = 'book';

      const response = await fetch('/api/v0/favourites', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, entityId, entityType }),
      });

      if (!response.ok) {
        throw new Error(`Failed to unfavourite asset: ${response.status}`);
      }
      setIsFavourited(false);
      setFavouriteCount(favouriteCount - 1);
    } catch (err) {
      setError(err as string);
    } finally {
      setLoading(false);
    }
  };

  return {
    isFavourited,
    loading,
    error,
    setFavourite,
    setUnfavourite,
    favouriteCount,
  };
}
